import { useState, useCallback, useMemo } from 'react';
import { useResearchLibraryQuery } from './library/use-research-library-query';
import { useDeleteResearch } from './library/use-delete-research';
import { useToggleResearchFavorite } from './library/use-toggle-research-favorite';
import { ResearchLibraryItem } from './library/types';
import { useResearchContext } from '@/contexts/research/ResearchContext';
import { useDebouncedValue } from '@/lib/utils';

interface UsePublicResearchDataProps {
  showFavorites?: boolean;
  showOnlyUserResearch?: boolean;
}

/**
 * Hook for fetching public research library data
 * This is a wrapper around useResearchLibraryQuery that provides
 * a consistent interface for the public research page
 */
export const usePublicResearchData = (props?: UsePublicResearchDataProps) => {
  const { showFavorites = false, showOnlyUserResearch = false } = props || {};
  const { state } = useResearchContext();
  
  // Use researchPrompt from context as the search filter (matching private research)
  const debouncedSearchQuery = useDebouncedValue(state.researchPrompt || '', 500);
  
  // Determine scope based on showOnlyUserResearch
  const scope = showOnlyUserResearch ? 'mine' : 'all';
  
  // Build filter options including search and other filters from ResearchContext
  const filterOptions = useMemo(() => ({
    scope,
    showFavorites,
    pageSize: 12,
    // Search query from the sidebar input
    searchQuery: debouncedSearchQuery || undefined,
    // Research type filter
    researchType: state.selectedType || undefined,
    // Advanced filters
    timeframe: state.timeframe && state.timeframe !== 'none' ? state.timeframe : undefined,
    country: state.country && state.country !== 'none' ? state.country : undefined,
    sourceType: state.sourceType && state.sourceType !== 'none' ? state.sourceType : undefined,
  }), [scope, showFavorites, debouncedSearchQuery, state.selectedType, state.timeframe, state.country, state.sourceType]);
  
  // Use the public research library query with all filters
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useResearchLibraryQuery(filterOptions);

  // Flatten pages data
  const allResearch = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.data);
  }, [data]);

  // Delete mutation
  const deleteResearchMutation = useDeleteResearch();

  // Toggle favorite mutation
  const toggleFavoriteMutation = useToggleResearchFavorite();

  // Handle delete with the mutation
  const handleDeleteResearch = useCallback((researchId: string) => {
    return deleteResearchMutation.mutateAsync(researchId);
  }, [deleteResearchMutation]);

  // Handle toggle favorite
  const handleToggleFavorite = useCallback(async (researchId: string) => {
    try {
      await toggleFavoriteMutation.mutateAsync(researchId);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  }, [toggleFavoriteMutation]);

  return {
    research: allResearch,
    allResearch, // For compatibility
    isLoading,
    error: error as Error | null,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    handleDeleteResearch,
    handleToggleFavorite,
    isDeleting: deleteResearchMutation.isPending,
    totalCount: data?.pages?.[0]?.count || 0,
  };
};